import { useCallback, useContext, useEffect, useRef } from 'react';
import { useAppDispatch } from '../../../grid/reduxStore/Store';
import { setColumnToActiveHoverColumn, resizeAdjacentColumns } from '../../../grid/reduxStore/editorSlice';
import { useSectionData } from '../../../Sections/useSectionContext.ts';
import { SelectionContext } from '../../SelectedBlockInfoProvider.tsx';
import { useTableContentUndoRedo } from '../../../hooks/useTableContentUndoRedo.ts';
import { useTableManipulation } from './useTableManipulation.ts';
import { MIN_COLUMN_WIDTH } from './defaultData.tsx';

interface ColumnResizeHandleProps {
  columnId: string;
  isActive: boolean;
  leftColumnId: string | null;
}

export function ColumnResizeHandle({ leftColumnId, columnId, isActive }: ColumnResizeHandleProps) {
  const { sectionId } = useSectionData();
  const { selectedBlockIdByWrapper } = useContext(SelectionContext);
  const blockId = selectedBlockIdByWrapper as string;
  const dispatch = useAppDispatch();
  const { getTableData } = useTableManipulation();
  const { captureBeforeEdit, commitEdit } = useTableContentUndoRedo();

  const dragState = useRef<{
    startX: number;
    startLeftWidth: number;
    startRightWidth: number;
  } | null>(null);
  const rafId = useRef<number | null>(null);
  const lastDelta = useRef<number>(0);

  const handleRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback(
    (moveEvent: MouseEvent) => {
      if (!dragState.current || !leftColumnId) return;

      const delta = moveEvent.clientX - dragState.current.startX;
      lastDelta.current = delta;

      if (rafId.current !== null) cancelAnimationFrame(rafId.current);

      rafId.current = requestAnimationFrame(() => {
        if (!dragState.current) return;
        const totalWidth = dragState.current.startLeftWidth + dragState.current.startRightWidth;
        const newLeftWidth = Math.max(MIN_COLUMN_WIDTH, Math.min(dragState.current.startLeftWidth + delta, totalWidth - MIN_COLUMN_WIDTH));
        dispatch(
          resizeAdjacentColumns({
            sectionId,
            blockId,
            leftColumnId,
            rightColumnId: columnId,
            newLeftWidth,
            newRightWidth: totalWidth - newLeftWidth,
          })
        );
        rafId.current = null;
      });
    },
    [dispatch, sectionId, blockId, columnId, leftColumnId]
  );

  const handleMouseUp = useCallback(() => {
    if (rafId.current !== null) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;

      if (dragState.current && leftColumnId) {
        const totalWidth = dragState.current.startLeftWidth + dragState.current.startRightWidth;
        const newLeftWidth = Math.max(
          MIN_COLUMN_WIDTH,
          Math.min(dragState.current.startLeftWidth + lastDelta.current, totalWidth - MIN_COLUMN_WIDTH)
        );
        dispatch(
          resizeAdjacentColumns({
            sectionId,
            blockId,
            leftColumnId,
            rightColumnId: columnId,
            newLeftWidth,
            newRightWidth: totalWidth - newLeftWidth,
          })
        );
      }
    }

    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    dispatch(setColumnToActiveHoverColumn({ sectionId, blockId, columnId, activeHoverColumn: false }));

    if (dragState.current && leftColumnId) {
      const totalWidth = dragState.current.startLeftWidth + dragState.current.startRightWidth;
      const finalLeftWidth = Math.max(
        MIN_COLUMN_WIDTH,
        Math.min(dragState.current.startLeftWidth + lastDelta.current, totalWidth - MIN_COLUMN_WIDTH)
      );
      const finalRightWidth = totalWidth - finalLeftWidth;
      const currentContent = getTableData(blockId, sectionId);

      if (currentContent) {
        const finalContent = {
          ...currentContent,
          columns: currentContent.columns.map((col) => {
            if (col.field === leftColumnId) return { ...col, width: finalLeftWidth };
            if (col.field === columnId) return { ...col, width: finalRightWidth };
            return col;
          }),
        };
        commitEdit(blockId, sectionId, finalContent);
      }
    }

    dragState.current = null;
    lastDelta.current = 0;

    window.removeEventListener('mousemove', stableHandleMouseMove);
    window.removeEventListener('mouseup', stableHandleMouseUp);
  }, [dispatch, sectionId, blockId, columnId, leftColumnId, handleMouseMove, getTableData, commitEdit]);

  const handleMouseEnter = useCallback(() => {
    dispatch(setColumnToActiveHoverColumn({ sectionId, blockId, columnId, activeHoverColumn: true }));
  }, [dispatch, sectionId, blockId, columnId]);

  const handleMouseLeave = useCallback(() => {
    if (!dragState.current) {
      dispatch(setColumnToActiveHoverColumn({ sectionId, blockId, columnId, activeHoverColumn: false }));
    }
  }, [dispatch, sectionId, blockId, columnId]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const columnEl = handleRef.current?.closest(`[data-field="${columnId}"]`) as HTMLElement | null;
      const rowEl = columnEl?.parentElement;
      const leftColumnEl = rowEl?.querySelector(`[data-field="${leftColumnId}"]`) as HTMLElement | null;

      dragState.current = {
        startX: e.clientX,
        startRightWidth: columnEl?.offsetWidth ?? 100,
        startLeftWidth: leftColumnEl?.offsetWidth ?? 100,
      };

      const currentContent = getTableData(blockId, sectionId);
      if (currentContent) captureBeforeEdit(currentContent);

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      window.addEventListener('mousemove', stableHandleMouseMove);
      window.addEventListener('mouseup', stableHandleMouseUp);
    },
    [columnId, leftColumnId, handleMouseMove, handleMouseUp, getTableData, captureBeforeEdit]
  );

  const handleMouseMoveRef = useRef(handleMouseMove);
  const handleMouseUpRef = useRef(handleMouseUp);

  useEffect(() => {
    handleMouseMoveRef.current = handleMouseMove;
  }, [handleMouseMove]);

  useEffect(() => {
    handleMouseUpRef.current = handleMouseUp;
  }, [handleMouseUp]);

  const stableHandleMouseMove = useCallback((e: MouseEvent) => {
    handleMouseMoveRef.current(e);
  }, []);

  const stableHandleMouseUp = useCallback(() => {
    handleMouseUpRef.current();
  }, []);

  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', stableHandleMouseMove);
      window.removeEventListener('mouseup', stableHandleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (rafId.current !== null) cancelAnimationFrame(rafId.current);
    };
  }, []);

  return (
    <div
      ref={handleRef}
      className={`py-cellWidthHandler ${isActive ? 'activeHoverColumn' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onClick={(e) => e.stopPropagation()}
    />
  );
}
